import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import PremiumGate from "@/components/PremiumGate";
import { api, downloadAuthed } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { ArrowLeft, GitCompareArrows, Loader2, RefreshCw, LayoutList, LayoutGrid, FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  extractSophistication,
  extractDominantMechanism,
  extractPrimaryObjective,
  SOPH_LABEL,
  SOPH_SCORE,
} from "@/lib/analysisParse";

function renderBold(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    const m = part.match(/^\*\*(.+)\*\*$/);
    if (m) return <strong key={`b-${idx}`}>{m[1]}</strong>;
    return <span key={`t-${idx}`}>{part}</span>;
  });
}

function MdLite({ text }) {
  const blocks = useMemo(() => {
    if (!text) return [];
    const lines = text.split("\n");
    const out = [];
    let buf = [];
    let inTable = false;
    let tableRows = [];
    const flushP = () => {
      if (buf.length) {
        out.push({ type: "p", text: buf.join("\n") });
        buf = [];
      }
    };
    const flushTable = () => {
      if (tableRows.length) {
        out.push({ type: "table", rows: tableRows });
        tableRows = [];
      }
      inTable = false;
    };
    for (const line of lines) {
      const m2 = line.match(/^##\s+(.+)$/);
      const bullet = line.match(/^[-*]\s+(.+)$/);
      const tableLine = line.trim().startsWith("|") && line.trim().endsWith("|");
      if (tableLine) {
        flushP();
        // skip separator row like | --- | --- |
        if (/^\|[\s:|-]+\|$/.test(line.trim())) continue;
        const cells = line.split("|").slice(1, -1).map((c) => c.trim());
        tableRows.push(cells);
        inTable = true;
        continue;
      } else if (inTable) {
        flushTable();
      }
      if (m2) {
        flushP();
        out.push({ type: "h2", text: m2[1].trim() });
        continue;
      }
      if (bullet) {
        flushP();
        const last = out[out.length - 1];
        if (last && last.type === "ul") last.items.push(bullet[1]);
        else out.push({ type: "ul", items: [bullet[1]] });
        continue;
      }
      if (line.trim() === "") {
        flushP();
      } else {
        buf.push(line);
      }
    }
    flushTable();
    flushP();
    return out;
  }, [text]);

  return (
    <div className="space-y-6">
      {blocks.map((b, i) => {
        if (b.type === "h2") {
          return (
            <h3
              key={`h-${i}`}
              className="font-serif-display text-2xl md:text-3xl font-bold uppercase tracking-tight border-l-2 border-[#D90429] pl-4"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={`u-${i}`} className="space-y-2 pl-2">
              {b.items.map((it, j) => (
                <li key={`li-${i}-${j}`} className="flex gap-3 font-sans-body text-base">
                  <span className="text-[#D90429] font-mono-tech text-xs mt-1.5">▪</span>
                  <span>{renderBold(it)}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === "table") {
          const [header, ...rows] = b.rows;
          return (
            <div key={`t-${i}`} className="overflow-x-auto border border-white/30" data-testid="md-table">
              <table className="w-full text-left">
                <thead className="bg-white/10">
                  <tr>
                    {(header || []).map((h, hi) => (
                      <th
                        key={hi}
                        className="font-mono-tech text-[10px] uppercase tracking-[0.2em] px-3 py-2 border-r border-white/20 last:border-r-0"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri} className="border-t border-white/20">
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="font-sans-body text-sm px-3 py-2 border-r border-white/20 last:border-r-0"
                        >
                          {renderBold(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <p
            key={`p-${i}`}
            className="font-sans-body text-base leading-relaxed whitespace-pre-wrap"
          >
            {renderBold(b.text)}
          </p>
        );
      })}
    </div>
  );
}

function DiscourseColumn({ item, t }) {
  if (!item) return null;
  return (
    <article
      className="border border-[#171717] bg-white p-5 flex flex-col min-w-[300px]"
      data-testid={`compare-col-${item.code}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
          · {item.code}
        </div>
        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-[#A3A3A3]">
          {item.model} · {item.language.toUpperCase()}
        </div>
      </div>
      <h2 className="font-serif-display text-xl font-bold leading-tight mb-3 line-clamp-3">
        {item.title}
      </h2>
      <div className="max-h-[360px] overflow-y-auto subtle-scroll border-t border-[#E5E5E5] pt-3 flex-1">
        <pre className="font-sans-body text-xs leading-relaxed text-[#262626] whitespace-pre-wrap">
          {item.result}
        </pre>
      </div>
    </article>
  );
}

function CompactColumn({ item, t }) {
  if (!item) return null;
  const soph = extractSophistication(item.result);
  const mech = extractDominantMechanism(item.result);
  const obj = extractPrimaryObjective(item.result);
  const sophLabel = soph ? SOPH_LABEL[soph] : "—";
  const sophFilled = soph ? SOPH_SCORE[soph] : 0;
  return (
    <article
      className="border border-[#171717] bg-white p-4 flex flex-col"
      data-testid={`compact-col-${item.code}`}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
          · {item.code}
        </div>
        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-[#A3A3A3]">
          {item.model}
        </div>
      </div>
      <h2 className="font-serif-display text-lg font-bold leading-tight mb-3 line-clamp-3">
        {item.title}
      </h2>
      <dl className="space-y-2 mt-auto border-t border-[#E5E5E5] pt-3">
        <div>
          <dt className="font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3]">
            {t("compact_soph_label")}
          </dt>
          <dd
            data-testid={`compact-soph-${item.code}`}
            className="mt-1 inline-flex items-center gap-2"
          >
            <div className="flex gap-0.5" data-testid={`compact-soph-bars-${item.code}`}>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 ${i <= sophFilled ? "bg-[#D90429]" : "bg-[#E5E5E5]"}`}
                />
              ))}
            </div>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#404040]">
              {sophLabel}
            </span>
          </dd>
        </div>
        <div>
          <dt className="font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3]">
            {t("compact_mech_label")}
          </dt>
          <dd className="font-serif-display text-base leading-snug mt-0.5 text-[#262626]">
            {mech || "—"}
          </dd>
        </div>
        <div>
          <dt className="font-mono-tech text-[9px] tracking-widest uppercase text-[#A3A3A3]">
            {t("compact_obj_label")}
          </dt>
          <dd className="font-serif-display text-base leading-snug mt-0.5 text-[#262626]">
            {obj || "—"}
          </dd>
        </div>
      </dl>
    </article>
  );
}

export default function Compare() {
  const [params] = useSearchParams();
  const { t, lang } = useLang();
  const { user } = useAuth();

  // accept both new ?ids=a,b,c and legacy ?a=&b=
  const idsList = useMemo(() => {
    const idsParam = params.get("ids");
    if (idsParam) return idsParam.split(",").map((s) => s.trim()).filter(Boolean);
    const a = params.get("a");
    const b = params.get("b");
    if (a && b) return [a, b];
    return [];
  }, [params]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState("claude");
  const [view, setView] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customSubtitle, setCustomSubtitle] = useState("");
  const [customNote, setCustomNote] = useState("");

  const downloadCorpusBriefing = async () => {
    if (idsList.length < 2) return;
    setBriefingLoading(true);
    try {
      const token = localStorage.getItem("gdas_token");
      const body = { ids: idsList, lang };
      if (customTitle.trim()) body.title = customTitle.trim();
      if (customSubtitle.trim()) body.subtitle = customSubtitle.trim();
      if (customNote.trim()) body.editor_note = customNote.trim();
      const resp = await fetch(`${api.defaults.baseURL}/briefings/corpus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(err || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GalascioDAS-briefing-corpus-N${idsList.length}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("✓");
    } catch (err) {
      toast.error(err?.message || "Error");
    } finally {
      setBriefingLoading(false);
    }
  };

  // Load controlled list of compare models + their cost estimates (no hardcoded prices)
  useEffect(() => {
    let cancelled = false;
    api
      .get("/compare/models")
      .then((r) => {
        if (cancelled) return;
        setAvailableModels(r.data?.models || []);
        setDefaultModel(r.data?.default || "claude");
        if (!model) setModel(r.data?.default || "claude");
      })
      .catch(() => {
        if (cancelled) return;
        // graceful fallback — keep selector hidden, default to claude
        setAvailableModels([]);
        if (!model) setModel("claude");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync default view from N (compact for >=4, full otherwise)
  useEffect(() => {
    if (view !== null) return;
    if (idsList.length >= 4) setView("compact");
    else if (idsList.length >= 2) setView("full");
  }, [idsList.length, view]);

  const run = useCallback(async (forceModel) => {
    if (idsList.length < 2) return;
    setLoading(true);
    try {
      const r = await api.post("/compare", {
        ids: idsList,
        language: lang,
        model: forceModel || model,
      });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [idsList, lang, model]);

  // Show a notice when the user picks a model different from the one used in the last result.
  // No auto re-run — user must explicitly press "Regenerar comparación" to spend LLM budget.
  const modelChangedSinceRun =
    !!data && !!data.model && !!model && data.model !== model && !loading;

  useEffect(() => {
    if (idsList.length >= 2) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(idsList)]);

  // Premium gate: free/beta users must see the upgrade prompt regardless of URL params.
  if (user && !user.is_premium) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="compare-page">
        <Header />
        <PremiumGate testId="compare-premium-gate" />
      </div>
    );
  }

  if (idsList.length < 2) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-[900px] mx-auto px-6 py-20 text-center">
          <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#D90429] mb-4">
            · COMPARADOR
          </div>
          <h1 className="font-serif-display text-4xl font-bold mb-6">{t("compare_title")}</h1>
          <p className="text-[#404040] mb-8">{t("compare_pick_two")}</p>
          <Link
            to="/app"
            className="px-6 py-3 border border-[#0A0A0A] font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#0A0A0A] hover:text-white"
          >
            <ArrowLeft className="w-3 h-3" /> {t("compare_back")}
          </Link>
        </div>
      </div>
    );
  }

  const n = data?.items?.length ?? idsList.length;
  const effectiveView = view || (n >= 4 ? "compact" : "full");
  // Compact view tolerates more columns per row
  const gridCols = effectiveView === "compact"
    ? (n <= 2 ? "md:grid-cols-2"
      : n === 3 ? "md:grid-cols-3"
      : "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4")
    : (n <= 2 ? "md:grid-cols-2"
      : n === 3 ? "md:grid-cols-3"
      : n === 4 ? "md:grid-cols-2 lg:grid-cols-4"
      : "md:grid-cols-2 lg:grid-cols-3");

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="compare-page">
      <Header />
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-8 md:py-12 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/app"
              data-testid="compare-back"
              className="font-mono-tech text-xs uppercase tracking-[0.3em] inline-flex items-center gap-2 hover:text-[#D90429] mb-3"
            >
              <ArrowLeft className="w-3 h-3" /> {t("compare_back")}
            </Link>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-2">
              · MODO · COMPARATIVO · {n} DISCURSOS
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-bold leading-none tracking-tight">
              {t("compare_title")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-[#0A0A0A]" data-testid="compare-model-selector">
              {(availableModels.length > 0
                ? availableModels
                : [
                    { id: "claude", label: "Claude Sonnet 4.5", cost_estimated: null },
                    { id: "openai", label: "GPT-5.2", cost_estimated: null },
                    { id: "gemini", label: "Gemini 3 Pro", cost_estimated: null },
                  ]
              ).map((m) => {
                const isActive = model === m.id;
                const costStr =
                  m.cost_estimated == null
                    ? lang === "es"
                      ? "coste n/d"
                      : "cost n/a"
                    : `~$${Number(m.cost_estimated).toFixed(4).replace(/0+$/, "0")}`;
                return (
                  <button
                    key={m.id}
                    data-testid={`compare-model-${m.id}`}
                    data-default={m.id === defaultModel ? "true" : "false"}
                    onClick={() => setModel(m.id)}
                    title={`${m.label} · ${costStr}`}
                    className={`px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 flex flex-col items-start gap-0.5 ${
                      isActive ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                    }`}
                  >
                    <span>{m.label.replace(" Sonnet 4.5", "").replace("GPT-", "GPT-").replace(" 3 Pro", "")}</span>
                    <span
                      className={`text-[8px] tracking-[0.15em] normal-case ${
                        isActive ? "text-white/70" : "text-[#A3A3A3]"
                      }`}
                      data-testid={`compare-model-${m.id}-cost`}
                    >
                      {costStr}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              data-testid="compare-regen"
              onClick={() => run()}
              disabled={loading}
              className={`px-4 py-2 border font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40 ${
                modelChangedSinceRun
                  ? "border-[#D90429] bg-[#D90429] text-white animate-pulse"
                  : "border-[#0A0A0A] hover:bg-[#D90429] hover:text-white hover:border-[#D90429]"
              }`}
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {t("compare_regenerate")}
            </button>
            <button
              data-testid="download-corpus-briefing"
              onClick={downloadCorpusBriefing}
              disabled={briefingLoading || idsList.length < 2}
              className="px-4 py-2 border border-[#0A0A0A] bg-[#0A0A0A] text-white font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#D90429] hover:border-[#D90429] disabled:opacity-40"
            >
              {briefingLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
              {lang === "es" ? "Briefing PDF" : "Briefing PDF"}
            </button>
            <button
              data-testid="toggle-customize-corpus"
              onClick={() => setCustomOpen((v) => !v)}
              className="font-mono-tech text-[10px] uppercase tracking-widest text-[#D90429] hover:underline"
            >
              {customOpen ? "−" : "+"} {lang === "es" ? "Personalizar" : "Customize"}
            </button>
          </div>
        </div>

        {/* Discourse columns - responsive grid */}
        {customOpen && (
          <div data-testid="customize-corpus-form" className="border border-[#171717] bg-white p-5 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
              · PERSONALIZAR BRIEFING (FIRMA DEL ANALISTA)
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                {lang === "es" ? "Título" : "Title"} ({customTitle.length}/80)
              </label>
              <input
                data-testid="custom-title-corpus"
                type="text"
                maxLength={80}
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder={lang === "es" ? "Reporte comparativo" : "Comparative briefing"}
                className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-sm focus:outline-none focus:border-[#D90429]"
              />
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                {lang === "es" ? "Subtítulo" : "Subtitle"} ({customSubtitle.length}/140)
              </label>
              <input
                data-testid="custom-subtitle-corpus"
                type="text"
                maxLength={140}
                value={customSubtitle}
                onChange={(e) => setCustomSubtitle(e.target.value)}
                placeholder={lang === "es" ? `Corpus de ${n} discursos` : `Corpus of ${n} discourses`}
                className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-sm focus:outline-none focus:border-[#D90429]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
                {lang === "es" ? "Contexto editorial del analista" : "Editor note"} ({customNote.length}/280)
              </label>
              <textarea
                data-testid="custom-note-corpus"
                maxLength={280}
                rows={2}
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
                placeholder={lang === "es"
                  ? "Una o dos líneas firmadas por el analista; aparecerán bajo el subtítulo del PDF."
                  : "One or two lines signed by the analyst; will appear under the PDF subtitle."}
                className="w-full border border-[#E5E5E5] focus:border-[#0A0A0A] bg-white px-3 py-2 text-sm focus:outline-none resize-none font-serif-display italic"
              />
            </div>
          </div>
        )}

        {modelChangedSinceRun && (
          <div
            data-testid="model-changed-notice"
            className="border border-[#D90429] bg-[#FFF4F4] px-4 py-3 flex items-start gap-3"
          >
            <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mt-0.5">
              · AVISO ·
            </span>
            <span className="font-sans-body text-sm text-[#262626]">
              {lang === "es"
                ? "Modelo cambiado. Pulsa "
                : "Model changed. Press "}
              <strong className="text-[#D90429]">
                {t("compare_regenerate")}
              </strong>
              {lang === "es" ? " para aplicar." : " to apply."}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
            · {n} DISCURSOS · VISTA {effectiveView === "compact" ? "COMPACTA" : "COMPLETA"}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
              {t("compare_view_label")}
            </span>
            <div className="flex border border-[#0A0A0A]" data-testid="view-toggle">
              <button
                data-testid="view-compact"
                onClick={() => setView("compact")}
                className={`px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest border-r border-[#0A0A0A] last:border-r-0 inline-flex items-center gap-1 ${
                  effectiveView === "compact" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                }`}
              >
                <LayoutGrid className="w-3 h-3" /> {t("compare_view_compact")}
              </button>
              <button
                data-testid="view-full"
                onClick={() => setView("full")}
                className={`px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${
                  effectiveView === "full" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F5F5F5]"
                }`}
              >
                <LayoutList className="w-3 h-3" /> {t("compare_view_full")}
              </button>
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 ${gridCols} gap-4`} data-testid="discourse-columns">
          {(data?.items || []).map((it) =>
            effectiveView === "compact" ? (
              <CompactColumn key={it.id} item={it} t={t} />
            ) : (
              <DiscourseColumn key={it.id} item={it} t={t} />
            )
          )}
        </div>

        {/* AI Comparison */}
        <section
          data-testid="compare-ai-section"
          className="bg-[#0A0A0A] text-white p-6 md:p-10 border-t-4 border-[#D90429]"
        >
          <div className="flex items-center gap-3 mb-6">
            <GitCompareArrows className="w-5 h-5 text-[#D90429]" />
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
              · GALASCIODAS · COMPARATIVO {n >= 3 ? `MASIVO · N=${n}` : ""}
            </div>
            {data?.model && (
              <div
                data-testid="compare-result-model"
                className="ml-auto font-mono-tech text-[9px] tracking-[0.2em] uppercase text-white/60 border border-white/30 px-2 py-1"
                title={
                  data?.cost_estimated == null
                    ? lang === "es"
                      ? "Coste estimado no disponible"
                      : "Estimated cost not available"
                    : `${lang === "es" ? "Coste estimado" : "Estimated cost"}: $${Number(
                        data.cost_estimated,
                      ).toFixed(4)}`
                }
              >
                {lang === "es" ? "generado con" : "generated with"} · {data.model}
                {data?.cost_estimated != null && (
                  <span className="ml-2 text-white/40">
                    ~${Number(data.cost_estimated).toFixed(4)}
                  </span>
                )}
                {data?.cost_estimated == null && (
                  <span className="ml-2 text-white/40">
                    · {lang === "es" ? "coste n/d" : "cost n/a"}
                  </span>
                )}
              </div>
            )}
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold mb-8 uppercase">
            {t("compare_section_ai")}
          </h2>
          {loading ? (
            <div className="font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("compare_loading")}
            </div>
          ) : data?.comparison ? (
            <MdLite text={data.comparison} />
          ) : (
            <div className="font-mono-tech text-xs uppercase tracking-widest opacity-60">···</div>
          )}
        </section>
      </div>
    </div>
  );
}
